package ui.campaign.detailv2.editor.page

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CutCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.Arrow_top_left_thick
import compose.assets.svg.Minus_thick
import compose.assets.svg.Plus
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.*
import tk.mallumo.utils.noDiacriticsLower
import tools.await
import tools.rememberString
import tools.roundDecimals
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.NextButton
import ui.campaign.detailv2.editor.feature.FeatureCounter
import ui.campaign.detailv2.editor.widget.DialogDeviceHeatMap
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperELocation
import ui.snackBarStateGlobal
import ui.widget.DialogState
import ui.widget.IconicSwitch
import ui.widget.Space
import kotlin.math.roundToLong

@Composable
fun CampaignEditorScope.CampaignPage01Location(nnvl: CampaignWrapperE) {
    if (!nnvl.location.counter.isLoading) {
        ListingContent(nnvl)
    }
}

@Composable
@OptIn(ExperimentalFoundationApi::class)
private fun CampaignEditorScope.ListingContentInternal(
    nnvl: CampaignWrapperE,
    contentPadding: PaddingValues = PaddingValues(vertical = 0.dp),
    modifier: Modifier,
    headerContent: LazyListScope.() -> Unit = {}
) {
    LazyColumn(
        modifier = modifier,
        contentPadding = contentPadding,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        headerContent()
        items(nnvl.location.locations.state) {
            LocationItem(it, nnvl.general.isCommercial.state)
        }
        item {
            NextButton()
        }
        item {
            Space(64.dp)
        }
    }
}

@Composable
@OptIn(ExperimentalFoundationApi::class)
private fun CampaignEditorScope.ListingContent(
    nnvl: CampaignWrapperE
) {

    if (display.isMobileOrSmall) {
        ListingContentInternal(
            nnvl = nnvl,
            contentPadding =
            if (nnvl.general.isCommercial.state) PaddingValues(0.dp)
            else PaddingValues(16.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            if (nnvl.general.isCommercial.state) {
                stickyHeader {
                    Overview(nnvl, Modifier.fillMaxWidth(), true)
                }
            }
        }
    } else {
        Row(Modifier.fillMaxSize()) {
            Box(Modifier.weight(1F)) {
                ListingContentInternal(
                    nnvl = nnvl,
                    contentPadding = PaddingValues(vertical = 16.dp),
                    modifier = Modifier
                        .let {
                            if (display.resolutionDp.width < (1024F * 1.5F).dp) it.fillMaxWidth(0.8F)
                            else it.width(921.dp)
                        }
                        .fillMaxHeight()
                        .align(Alignment.TopCenter)
                )
            }
            if (nnvl.general.isCommercial.state) {
                Overview(nnvl, Modifier.width(384.dp).fillMaxHeight(), false)
            }
        }
    }
}

@Composable
private fun CampaignEditorScope.Overview(
    nnvl: CampaignWrapperE,
    modifier: Modifier,
    onlyBasicInfo: Boolean
) {
    val scope = rememberCoroutineScope()

    @Composable
    fun LineRow(key: String, value: String) {
        Row(
            Modifier.padding(4.dp)
                .let {
                    if (!onlyBasicInfo) it
                    else it.padding(horizontal = 16.dp)
                }) {
            Text(
                text = key,
                modifier = Modifier.weight(1F)
            )
            Text(
                text = value
            )
        }
    }

    Column(
        modifier.background(MaterialTheme.colorScheme.surfaceContainer)
            .padding(8.dp),
        verticalArrangement = Arrangement.spacedBy(if (onlyBasicInfo) 4.dp else 8.dp)
    ) {
        LineRow(
            key = Res.string.layer_top_detail_editor__location_devices.rememberString(),
            value = buildString {
                append(nnvl.location.locations.state.sumOf { it.devices.state.count { it.active.state } })
                append(" / ")
                append(nnvl.location.locations.state.sumOf { it.devices.state.count() })
            }
        )
        LineRow(
            key = Res.string.layer_top_detail_editor__location_impression_expect.rememberString(),
            value = nnvl.location.locations.state.sumOf { it.devices.state.filter { it.active.state }.sumOf { it.impressionSum } }
                .let { it.toDouble() * 0.95 }
                .roundToLong()
                .toString()
                .reversed()
                .chunked(3)
                .joinToString("'")
                .reversed()
        )

        LineRow(
            key = Res.string.layer_top_detail_editor__location_impression_max.rememberString(),
            value = nnvl.location.locations.state.sumOf { it.devices.state.filter { it.active.state }.sumOf { it.impressionSum } }
                .toString()
                .reversed()
                .chunked(3)
                .joinToString("'")
                .reversed()
        )
        if (!onlyBasicInfo) {
            Spacer(Modifier.weight(1F))
            Button(
                modifier = Modifier.fillMaxWidth(),
                enabled = nnvl.location.locations.state.any { it.devices.state.any { !it.active.state } },
                onClick = {
                    nnvl.location.locations.state.forEach { devices ->
                        devices.devices.state
                            .filter { it.goalAccomplishment >= 100F }
                            .forEach { it.active.set(true) }
                    }
                    val criticalDevices = nnvl.location.locations.state.sumOf { it.devices.state.count { it.goalAccomplishment < 100F } }
                    if (criticalDevices > 0) {
                        scope.launch {
                            snackBarStateGlobal.showSnackbar(Res.string.layer_top_detail_editor__err_location_no_devices.await(criticalDevices.toString()))
                        }
                    }
                }) {
                Text(
                    text = Res.string.layer_top_detail_editor__location_devices_all.rememberString(),
                    fontWeight = FontWeight.Bold
                )
            }

            Button(
                modifier = Modifier.fillMaxWidth(),
                enabled = nnvl.location.locations.state.any { it.devices.state.any { it.active.state } },
                onClick = {
                    nnvl.location.locations.state.forEach { devices ->
                        devices.devices.state.forEach { it.active.set(false) }
                    }
                }) {
                Text(
                    text = Res.string.layer_top_detail_editor__location_devices_none.rememberString(),
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}


@Composable
private fun CampaignEditorScope.LocationItem(item: CampaignWrapperELocation.AdapterLocation.Item, isCommercial: Boolean) {
    val scope = rememberCoroutineScope()
    Card(
        modifier = Modifier.padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(horizontal = 8.dp)
        ) {
            IconButton(
                enabled = item.devices.state.any { !it.active.state },
                modifier = Modifier.align(Alignment.CenterVertically),
                onClick = {
                    item.devices.state
                        .filter { it.goalAccomplishment >= 100F }
                        .filterNot { it.active.state }
                        .forEach { it.active.set(true) }
                    val criticalDevices = item.devices.state.count { it.goalAccomplishment < 100F }
                    if (criticalDevices > 0) {
                        scope.launch {
                            snackBarStateGlobal.showSnackbar(Res.string.layer_top_detail_editor__err_location_no_devices.await(criticalDevices.toString()))
                        }
                    }
                }) {
                Icon(Svg.Plus, null)
            }

            Text(
                text = item.data.name,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1F)
                    .align(Alignment.CenterVertically)
                    .padding(vertical = 8.dp)
            )


            IconButton(
                enabled = item.devices.state.any { it.active.state },
                modifier = Modifier.align(Alignment.CenterVertically),
                onClick = {
                    item.devices.state
                        .filter { it.active.state }
                        .forEach { it.active.set(false) }
                }) {
                Icon(Svg.Minus_thick, null)
            }
        }
        item.devices.state
            .sortedWith(compareBy({ it.goalAccomplishment * -1F }, { it.data.name.noDiacriticsLower }))
            .forEach {
                Card(modifier = Modifier.padding(start = 8.dp, end = 8.dp)) {
                    Device(item.ohSpecs, it, isCommercial)
                }
                Space(8.dp)
            }
        Space(4.dp)
    }
}

@Composable
private fun CampaignEditorScope.Device(
    ohSpace: FeatureCounter.LocationTimeOH,
    item: CampaignWrapperELocation.AdapterDevice.Item,
    isCommercial: Boolean
) {
    val heatMapState = DialogState.remember(key = "HM-${item.key}")
    DialogDeviceHeatMap(
        state = heatMapState,
        ohSpace = ohSpace,
        device = item
    )
    Column {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier
                .clickable(item.countNotUsableDays == 0) {
                    item.active.set(!item.active.state)
                }
                .padding(start = 8.dp)
        ) {
            if (item.countNotUsableDays == 0) {
                IconicSwitch(
                    checked = item.active.state,
                    onCheckedChange = { item.active.set(it) },
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .width(64.dp),
                    colors = SwitchDefaults.colors(
                        MaterialTheme.colorScheme.primaryContainer,
                        MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )
            } else {
                Space(4.dp)
            }
            Text(
                text = item.data.name,
                style = MaterialTheme.typography.titleMedium,
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
                modifier = Modifier.weight(1F)
                    .align(Alignment.CenterVertically)
            )
            if (isCommercial) DevicePercenageAction(item, heatMapState)

        }
        if (isCommercial) CriticalSpaceSubmenu(item)
    }
}

@Composable
private fun CriticalSpaceSubmenu(item: CampaignWrapperELocation.AdapterDevice.Item) {
    @Composable
    fun LineRow(key: String, value: String) {
        Row(Modifier.padding(4.dp)) {
            Text(
                text = key,
                modifier = Modifier.weight(1F)
            )
            Text(
                text = value
            )
        }
    }

    if (item.countNotUsableDays != 0) {
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier
                .padding(8.dp)
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .background(MaterialTheme.colorScheme.surfaceContainerHigh, CutCornerShape(8.dp))
                    .clip(CutCornerShape(8.dp))
                    .clickable { item.active.set(false) }
                    .padding(8.dp)) {
                RadioButton(!item.active.state, null)
                Text(
                    text = Res.string.layer_top_detail_editor__location_device_limited_select_no.rememberString(),
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .weight(1F)
                )
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .background(MaterialTheme.colorScheme.surfaceContainerHigh, CutCornerShape(8.dp))
                    .clip(CutCornerShape(8.dp))
                    .clickable(item.countUsableDays != 0) { item.active.set(true) }
                    .padding(8.dp)) {
                if (item.countUsableDays != 0) {
                    RadioButton(
                        selected = item.active.state,
                        onClick = null,
                        modifier = Modifier.padding(top = 4.dp)
                    )
                } else {
                    Space(24.dp)
                }

                Column {
                    LineRow(
                        key = Res.string.layer_top_detail_editor__location_device_limited_select_yes_goal.rememberString(),
                        value = "${item.goalAccomplishment.roundDecimals(2)}%"
                    )
                    Spacer(
                        modifier = Modifier
                            .padding(top = 2.dp)
                            .padding(horizontal = 4.dp)
                            .fillMaxWidth()
                            .height(0.5.dp)
                            .background(Color.White.copy(0.4F))
                    )
                    LineRow(
                        key = Res.string.layer_top_detail_editor__location_device_limited_select_yes_excluded_days.rememberString(),
                        value = "${item.countNotUsableDays} / ${item.countAllDays}"
                    )
                    Spacer(
                        modifier = Modifier
                            .padding(top = 2.dp)
                            .padding(horizontal = 4.dp)
                            .fillMaxWidth()
                            .height(0.5.dp)
                            .background(Color.White.copy(0.4F))
                    )
                    LineRow(
                        key = Res.string.layer_top_detail_editor__location_device_limited_select_yes_playback_days.rememberString(),
                        value = "${item.countUsableDays} / ${item.countAllDays}"
                    )
                }

            }
        }
    }
}

@Composable
private fun DevicePercenageAction(
    item: CampaignWrapperELocation.AdapterDevice.Item,
    heatMapState: DialogState
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier
            .background(item.maxUsageFutureColor, CardDefaults.shape)
            .clip(CardDefaults.shape)
            .clickable {
                heatMapState.show()
            }
            .padding(8.dp)
    ) {
        Text(
            text = "${
                if (item.usageNow >= 100F) 100
                else item.usageNow.roundDecimals(0)
            }%",
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
        )
        Icon(
            imageVector = Svg.Arrow_top_left_thick,
            contentDescription = null,
            modifier = Modifier.align(Alignment.CenterVertically)
                .rotate(135F)
        )
        Text(
            text = "${
                if (item.maxUsageFuture > 100F) item.maxUsageFuture.roundDecimals(2)
                else item.maxUsageFuture.roundDecimals(0)
            }%",
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
        )
    }
}

